import React, { useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';

interface TileInputPanelProps {
    wordBank: string[];
    placeholder?: string;
    onChange?: (value: string) => void;
    onSubmit?: (value: string) => void;
}

/**
 * Tile (word / letter) input panel extracted from SpellingActivity and SentenceBuildActivity.
 *
 * Students tap tiles to build a sentence or spell a word. Tapping a selected
 * tile removes it (toggle behaviour).
 */
export const TileInputPanel: React.FC<TileInputPanelProps> = ({
    wordBank,
    placeholder = 'Tap tiles to build your answer...',
    onChange,
    onSubmit,
}) => {
    const [selectedWords, setSelectedWords] = useState<string[]>([]);

    const handleWordTap = (word: string) => {
        setSelectedWords((prev) => {
            const exists = prev.includes(word);
            const next = exists ? prev.filter((w) => w !== word) : [...prev, word];
            const sentence = next.join(' ');
            onChange?.(sentence);
            return next;
        });
    };

    const sentence = selectedWords.join(' ');

    return (
        <View style={styles.panel}>
            <View style={styles.wordBank}>
                {wordBank.map((word, idx) => {
                    const selected = selectedWords.includes(word);
                    return (
                        <TouchableOpacity
                            testID={`tile-${word}`}
                            key={`${word}-${idx}`}
                            style={[styles.wordChip, selected && styles.wordChipSelected]}
                            onPress={() => handleWordTap(word)}
                            activeOpacity={0.6}
                            accessibilityRole="button"
                            accessibilityLabel={`Word ${word}${selected ? ', selected' : ''}`}
                        >
                            <Text style={[styles.wordChipText, selected && styles.wordChipTextSelected]} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                                {word}
                            </Text>
                        </TouchableOpacity>
                    );
                })}
            </View>
            <View style={styles.sentencePreview}>
                <Text style={styles.sentencePreviewText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{sentence || placeholder}</Text>
            </View>
            <TouchableOpacity
                testID="tile-submit"
                style={styles.submitBtn}
                onPress={() => {
                    if (sentence) onSubmit?.(sentence);
                }}
                activeOpacity={0.7}
                accessibilityRole="button"
                accessibilityLabel="Submit"
                accessibilityHint="Submits your sentence"
            >
                <Text style={styles.submitBtnText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Submit</Text>
            </TouchableOpacity>
        </View>
    );
};

const styles = StyleSheet.create({
    panel: {
        // flexGrow + flexBasis 'auto' (not flex:1): fills bounded parents but
        // sizes to content inside indefinite-height containers, where a
        // zero-basis flex would collapse to 0 height under Yoga.
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: 'auto',
        padding: 16,
        justifyContent: 'center',
    },
    wordBank: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 10,
        justifyContent: 'center',
        marginBottom: 16,
    },
    wordChip: {
        paddingHorizontal: 16,
        paddingVertical: 12,
        borderRadius: 12,
        backgroundColor: 'rgba(255,255,255,0.06)',
        borderWidth: 2,
        borderColor: 'rgba(255,255,255,0.1)',
    },
    wordChipSelected: {
        backgroundColor: 'rgba(99,102,241,0.25)',
        borderColor: '#6366f1',
    },
    wordChipText: {
        fontSize: 18,
        fontWeight: '700',
        color: '#94a3b8',
    },
    wordChipTextSelected: {
        color: '#f1f5f9',
    },
    sentencePreview: {
        backgroundColor: 'rgba(255,255,255,0.06)',
        borderRadius: 16,
        padding: 16,
        marginBottom: 16,
        borderWidth: 2,
        borderColor: 'rgba(255,255,255,0.1)',
        minHeight: 60,
        justifyContent: 'center',
    },
    sentencePreviewText: {
        fontSize: 20,
        fontWeight: '700',
        color: '#f1f5f9',
        textAlign: 'center',
    },
    submitBtn: {
        marginTop: 16,
        backgroundColor: '#6366f1',
        borderRadius: 16,
        paddingVertical: 16,
        alignItems: 'center',
    },
    submitBtnText: {
        color: '#fff',
        fontSize: 18,
        fontWeight: '800',
    },
});
